import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../App";
import ClientLayout from "../../components/portal/ClientLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  CreditCard,
  CheckCircle,
  AlertCircle,
  Download,
  Receipt,
} from "lucide-react";

const ClientInvoices = () => {
  const { token, API } = useAuth();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);

  useEffect(() => {
    fetchInvoices();
    
    // Check for payment callback
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${API}/client/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setInvoices(data);
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error("Could not verify payment status. Please refresh.");
      return;
    }

    try {
      const response = await fetch(`${API}/client/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.payment_status === "paid") {
        toast.success("Payment successful!");
        fetchInvoices();
      } else if (data.status === "expired") {
        toast.error("Payment session expired");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    setProcessingPayment(invoiceId);
    
    try {
      const response = await fetch(`${API}/client/invoices/${invoiceId}/pay`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || "Payment failed");

      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
      setProcessingPayment(null);
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await fetch(`${API}/client/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to download PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Invoice PDF downloaded");
    } catch (error) {
      toast.error("Failed to download invoice PDF");
    }
  };

  const handleDownloadReceipt = async (invoiceId) => {
    try {
      const response = await fetch(`${API}/client/invoices/${invoiceId}/receipt`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to download receipt");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Receipt_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Receipt downloaded");
    } catch (error) {
      toast.error(error.message || "Failed to download receipt");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const pendingInvoices = invoices.filter((i) => i.status === "pending");
  const paidInvoices = invoices.filter((i) => i.status === "paid");

  return (
    <ClientLayout activeNav="invoices">
      <div>
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-xl font-bold text-rnb-navy">Invoices</h1>
            <p className="text-sm text-gray-400 mt-0.5">View and pay your invoices securely.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-rnb-teal/30 border-t-rnb-teal rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pending Invoices */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-card" data-testid="pending-invoices">
              <div className="px-5 pt-5 pb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                </div>
                <span className="text-sm font-semibold text-rnb-navy">Pending Payment ({pendingInvoices.length})</span>
              </div>
              <div className="px-5 pb-5">
                {pendingInvoices.length > 0 ? (
                  <div className="space-y-3">
                    {pendingInvoices.map((invoice) => (
                      <div
                        key={invoice.invoice_id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-[#f8fafc] rounded-lg border border-gray-100"
                        data-testid={`invoice-${invoice.invoice_id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-rnb-navy text-sm">{invoice.description}</h3>
                            <Badge variant="outline" className="text-[10px]">Pending</Badge>
                          </div>
                          <p className="text-xs text-gray-400">Due {formatDate(invoice.due_date)}</p>
                        </div>
                        <div className="flex items-center gap-3 justify-between sm:justify-end">
                          <p className="font-mono text-lg sm:text-xl font-bold text-rnb-navy">{formatCurrency(invoice.amount)}</p>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(invoice.invoice_id)} className="h-8 w-8 p-0" data-testid={`download-pdf-${invoice.invoice_id}`}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              onClick={() => handlePayInvoice(invoice.invoice_id)}
                              disabled={processingPayment === invoice.invoice_id}
                              className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-lg text-xs h-8 px-4"
                              data-testid={`pay-invoice-${invoice.invoice_id}`}
                            >
                              {processingPayment === invoice.invoice_id ? "Processing..." : (
                                <><CreditCard className="h-3.5 w-3.5 mr-1.5" /> Pay Now</>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-8 text-sm">No pending invoices. You&apos;re all caught up!</p>
                )}
              </div>
            </div>

            {/* Paid Invoices */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-card" data-testid="paid-invoices">
              <div className="px-5 pt-5 pb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                </div>
                <span className="text-sm font-semibold text-rnb-navy">Payment History ({paidInvoices.length})</span>
              </div>
              <div className="px-5 pb-5">
                {paidInvoices.length > 0 ? (
                  <div className="space-y-2">
                    {paidInvoices.map((invoice) => (
                      <div
                        key={invoice.invoice_id}
                        className="flex items-center justify-between p-3 bg-[#f8fafc] rounded-lg border border-gray-50"
                        data-testid={`paid-invoice-${invoice.invoice_id}`}
                      >
                        <div>
                          <h3 className="font-medium text-rnb-navy text-sm">{invoice.description}</h3>
                          <p className="text-xs text-gray-400">Paid {formatDate(invoice.paid_at || invoice.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="font-mono font-semibold text-rnb-navy text-sm">{formatCurrency(invoice.amount)}</p>
                          <Button variant="outline" size="sm" onClick={() => handleDownloadReceipt(invoice.invoice_id)} className="h-7 w-7 p-0" title="Download Receipt" data-testid={`download-receipt-${invoice.invoice_id}`}>
                            <Receipt className="h-3 w-3" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(invoice.invoice_id)} className="h-7 w-7 p-0" title="Download Invoice" data-testid={`download-paid-pdf-${invoice.invoice_id}`}>
                            <Download className="h-3 w-3" />
                          </Button>
                          <Badge variant="secondary" className="text-[10px]">Paid</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-8 text-sm">No payment history yet.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientInvoices;
